import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Plus, Trash2, Upload, Image } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    original_price: '',
    category: 'Cushion Covers',
    craft_type: 'Zardozi',
    images: [],
    stock: '',
    featured: false,
    new_arrival: false
  });

  const categories = ['Cushion Covers', 'Bedspreads', 'Table Runners', 'Wall Hangings', 'Curtains'];

  useEffect(() => {
    if (isEdit) fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setFormData({
        ...response.data,
        price: response.data.price.toString(),
        original_price: response.data.original_price?.toString() || '',
        stock: response.data.stock.toString()
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      alert('Failed to load product');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const newImageUrls = [];

    for (const file of files) {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert(`"${file.name}" is not a valid format. Only JPEG and PNG are allowed.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert(`"${file.name}" exceeds 5MB limit.`);
        continue;
      }

      try {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/upload-image`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        newImageUrls.push(BACKEND_URL + res.data.url);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        alert(`Failed to upload "${file.name}". ${err.response?.data?.detail || ''}`);
      }
    }

    if (newImageUrls.length > 0) {
      setFormData(prev => ({ ...prev, images: [...prev.images, ...newImageUrls] }));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) {
      alert('Please upload at least one product image.');
      return;
    }
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        stock: parseInt(formData.stock),
      };

      if (isEdit) {
        await axios.put(`${API}/products/${id}`, submitData);
      } else {
        await axios.post(`${API}/products`, submitData);
      }
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product');
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-serif text-[#3E2723]">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h1>
          <button onClick={() => navigate('/admin/products')} className="flex items-center space-x-2 text-[#7A6B5D] hover:text-[#C4A265] transition-colors">
            <X className="w-5 h-5" /><span>Cancel</span>
          </button>
        </div>

        <div className="bg-[#FEFCF9] border border-[#E8DCC8] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Product Name *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required
                className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none"
                placeholder="e.g., Royal Zardozi Cushion Cover" />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Description *</label>
              <textarea name="description" value={formData.description} onChange={handleChange} required rows={4}
                className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none"
                placeholder="Describe the product, craftsmanship, and unique features..." />
            </div>

            {/* Price Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Current Price *</label>
                <input type="number" name="price" value={formData.price} onChange={handleChange} required min="0" step="0.01"
                  className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none" placeholder="4500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Original Price <span className="text-xs text-[#A09486] normal-case">(Optional)</span></label>
                <input type="number" name="original_price" value={formData.original_price} onChange={handleChange} min="0" step="0.01"
                  className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none" placeholder="6000" />
              </div>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Product Category *</label>
              <select name="category" value={formData.category} onChange={handleChange} required
                className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none bg-white">
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            {/* Stock + Featured */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Stock Quantity *</label>
                <input type="number" name="stock" value={formData.stock} onChange={handleChange} required min="0"
                  className="w-full px-4 py-3 border border-[#E8DCC8] focus:border-[#C4A265] focus:outline-none" placeholder="10" />
              </div>
              <div className="flex items-center pt-8 gap-8">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input type="checkbox" name="featured" checked={formData.featured} onChange={handleChange}
                    className="w-5 h-5 text-[#C4A265] border-[#E8DCC8] rounded focus:ring-[#C4A265]" />
                  <span className="text-sm font-medium text-[#3E2723]">Featured Product</span>
                </label>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input type="checkbox" name="new_arrival" checked={formData.new_arrival} onChange={handleChange}
                    className="w-5 h-5 text-[#C4A265] border-[#E8DCC8] rounded focus:ring-[#C4A265]" />
                  <span className="text-sm font-medium text-[#3E2723]">New Arrival</span>
                </label>
              </div>
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-[#3E2723] mb-2 tracking-wider uppercase">Product Images *</label>
              
              {/* Upload Area */}
              <div
                className="border-2 border-dashed border-[#E8DCC8] hover:border-[#C4A265] transition-colors p-8 text-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="image-upload-input"
                />
                {uploading ? (
                  <div>
                    <div className="inline-block w-8 h-8 border-2 border-[#E8DCC8] border-t-[#C4A265] rounded-full animate-spin mb-3"></div>
                    <p className="text-[#7A6B5D] text-sm">Uploading...</p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-[#C4A265] mx-auto mb-3" />
                    <p className="text-[#3E2723] font-medium mb-1">Click to upload images</p>
                    <p className="text-xs text-[#A09486]">JPEG or PNG, max 5MB each</p>
                  </div>
                )}
              </div>

              {/* Image Previews */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-4">
                  {formData.images.map((url, index) => (
                    <div key={index} className="relative group aspect-square bg-[#F5EFE6] border border-[#E8DCC8] overflow-hidden">
                      <img src={url} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Remove image"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-2 left-2 bg-[#3E2723] text-white text-[10px] px-2 py-0.5 tracking-wider uppercase">Main</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#A09486] mt-2">{formData.images.length} image{formData.images.length !== 1 ? 's' : ''} uploaded. First image will be used as the main product photo.</p>
            </div>

            {/* Submit */}
            <div className="flex space-x-4 pt-6 border-t border-[#E8DCC8]">
              <button type="submit" disabled={loading}
                className="flex items-center space-x-2 bg-[#3E2723] text-[#FEFCF9] px-8 py-3 font-semibold hover:bg-[#C4A265] transition-all disabled:opacity-50 disabled:cursor-not-allowed tracking-wider uppercase text-sm">
                <Save className="w-5 h-5" />
                <span>{loading ? 'Saving...' : (isEdit ? 'Update Product' : 'Add Product')}</span>
              </button>
              <button type="button" onClick={() => navigate('/admin/products')}
                className="px-8 py-3 border border-[#E8DCC8] text-[#7A6B5D] font-semibold hover:bg-[#F5EFE6] transition-colors tracking-wider uppercase text-sm">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProductForm;
